/**
 * A one-way channel from code that has no React context — the MutationCache,
 * axios interceptors — to the single Snackbar that renders for the whole app.
 *
 * It is a plain module rather than a context because the QueryClient is built
 * at module scope, before any component mounts, so the global mutation handler
 * cannot call a hook. That also makes the channel testable without rendering.
 */

export interface ErrorNotice {
  id: number;
  message: string;
}

type Listener = (notice: ErrorNotice) => void;

/**
 * One user action can fail several mutations at once (a save that fans out
 * into two writes, a list where every row retries). Repeating one sentence
 * four times reads as four separate faults, so an identical message inside
 * this window is dropped.
 */
export const DUPLICATE_WINDOW_MS = 4000;

const listeners = new Set<Listener>();
let lastMessage: string | null = null;
let lastAt = 0;
let nextId = 1;

/**
 * Returns the notice that was delivered, or null when it was blank or a
 * duplicate. `now` is injectable so the dedupe window is testable without
 * fake timers.
 */
export function publishErrorNotice(message: string, now: number = Date.now()): ErrorNotice | null {
  const text = message.trim();
  if (!text) return null;
  if (text === lastMessage && now - lastAt < DUPLICATE_WINDOW_MS) return null;

  lastMessage = text;
  lastAt = now;
  const notice: ErrorNotice = { id: nextId, message: text };
  nextId += 1;
  listeners.forEach((listener) => listener(notice));
  return notice;
}

export function subscribeToErrorNotices(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Test seam: drops every subscriber and forgets the dedupe window. */
export function resetErrorNotices(): void {
  listeners.clear();
  lastMessage = null;
  lastAt = 0;
}
